import { create } from 'zustand';

import service from '@/services/index';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { deleteObjectField } from '@/utils/object';
import { arrayToMap } from '@/utils/util';

type State = {
  agentMap: Record<string, AgentT>;
  selectedAgent: string | null;
  createAgentModalStatus: boolean;
};

type Actions = {
  setState: StoreSetter<State>;
  setNewAgent: (agent: AgentT) => void;
  addNewAgent: (params: PostAgentParams) => void;
  removeAgent: (alias: string) => void;
  fetchAgentList: () => Promise<AgentT[]>;
  selectAgent: (alias: string) => void;
  changeAgentAttr: <K extends keyof AgentT>(
    alias: string,
    target: K,
    value: AgentT[K],
  ) => void;
  setAgentAttr: <K extends keyof AgentT>(
    alias: string,
    target: K,
    value: AgentT[K],
  ) => void;
  setDeleteAgent: (alias: string) => void;
  updateAgentTags: (alias: string, tags: string[]) => void;
  initAgentStore: () => void;
};

const initialState: State = {
  agentMap: {},
  selectedAgent: null,
  createAgentModalStatus: false,
};

const useAgentStore = create<State & Actions>((set, get) => ({
  ...initialState,
  setState: set,

  async fetchAgentList() {
    const res = await service.agent.fetchAgentList();
    set({ agentMap: arrayToMap(res, 'alias') });
    return res;
  },
  async addNewAgent(params) {
    const res = await service.agent.createAgent(params);
    get().setNewAgent(res);
  },
  async removeAgent(alias) {
    await service.agent.deleteAgent(alias);
    get().setDeleteAgent(alias);
  },
  async updateAgentTags(alias, tags) {
    await service.agent.updateAgentTags(alias, tags);
    get().setAgentAttr(alias, 'tags', tags);
  },
  selectAgent(alias) {
    const { setNewModal } = useDetailModalStore.getState();
    set({ selectedAgent: alias });
    if (alias)
      setNewModal({
        alias,
        type: DETAIL_MODAL_TYPE.AGENT,
        idx: 0,
      });
  },
  changeAgentAttr(alias, target, value) {
    service.agent.updateAgentAttr(alias, target, value);
    get().setAgentAttr(alias, target, value);
  },
  setDeleteAgent(alias) {
    const newMap = deleteObjectField(get().agentMap, alias);
    set({ agentMap: newMap });
  },
  setNewAgent(agent) {
    set({
      agentMap: {
        ...get().agentMap,
        [agent.alias]: agent,
      },
    });
  },
  setAgentAttr(alias, target, value) {
    let newMap = { ...get().agentMap };
    if (!newMap[alias]) return;

    let newItem = { ...newMap[alias], [target]: value };
    newMap[alias] = newItem;

    set({ agentMap: newMap });
  },
  initAgentStore() {
    set(initialState);
  },
}));

export default useAgentStore;
